import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorHandler, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BmwgMarketingPreferencesModule, BmwgRadiosModule, BmwgTooltipModule } from 'bmwg-angular-library';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { JourneyIndicatorComponent } from './shared/components/journey-indicator/journey-indicator.component';
import { SelectModelComponent } from './rttd/routes/select-model/select-model.component';
import { NotFoundComponent } from './shared/routes/not-found/not-found.component';
import { StringsService } from './shared/services/strings/strings.service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Utilities } from './shared/utilities/utilities';
import { OptionCardComponent } from './shared/components/option-card/option-card.component';
import { ListingsComponent } from './shared/components/listings/listings.component';
import { Interceptor } from './shared/components/loading/interceptor';
import { AppLoadModule } from './shared/PRE-LOAD/app-load.module';
import { SelectAppointmentComponent } from './rttd/routes/select-appointment/select-appointment.component';
import { CalendarComponent } from './shared/components/calendar/calendar.component';
import { OptionCardSelectedComponent } from './shared/components/option-card-selected/option-card-selected.component';
import { OptionCardRescheduleComponent } from './shared/components/option-card-reschedule/option-card-reschedule.component';
import { LengthRestrictedTextareaComponent } from './shared/components/length-restricted-textarea/length-restricted-textarea.component';
import { PersonalInformationComponent } from './rttd/routes/personal-information/personal-information.component';
import { DetailsFormComponent } from './shared/components/details-form/details-form.component';
import { BookingConfirmationComponent } from './rttd/routes/booking-confirmation/booking-confirmation.component';
import { ErrorService } from './shared/services/errors/error.service';
import { ReservationService } from './shared/services/reservation/reservation.service';
import { E2eService } from './shared/services/e2e/e2e.service';
import { HttpErrorInterceptor } from './shared/interceptors/http-error.interceptor';
import { ErrorPageComponent } from './shared/routes/error-page/error-page.component';
import { HttpMockBackendRequestInterceptor } from './shared/mock-backend/mock-backend.interceptor';
import { environment } from '../environments/environment';
import { NoTimeslotsMessageComponent } from './shared/components/no-timeslots-message/no-timeslots-message.component';
import { UserSelectionsService } from './shared/services/user-selections/user-selections.service';
import { CancellationService } from './shared/services/cancellation/cancellation.service';
import { RescheduleService } from './shared/services/reschedule/reschedule.service';
import { PageHeaderComponent } from './shared/components/page-header/page-header.component';
import { BookingCancellationComponent } from './shared/routes/booking-cancellation/booking-cancellation.component';
import { BookingRescheduleComponent } from './shared/routes/booking-reschedule/booking-reschedule.component';
import { HttpE2eInterceptor } from './shared/services/e2e/e2e.interceptor';
import {
    BookingCancellationConfirmationComponent
} from './shared/routes/booking-cancellation-confirmation/booking-cancellation-confirmation.component';
import { ProductService } from './shared/services/product/product.service';
import { DatesService } from './shared/services/dates/dates.service';
import { RoutingStateService } from './shared/services/routing-state/routing-state.service';
import { PickupLocationComponent } from './shared/components/pickup-location/pickup-location.component';
import { DeepLinkService } from './shared/services/deep-link/deep-link.service';
import { EmptyComponent } from './shared/routes/empty/empty.component';
import { CookieService } from 'ngx-cookie-service';

export const isMock = environment.mock;

//Event imports
import { EventsComponent } from './events/routes/events/events.component'
import { EventConfirmationComponent } from './events/routes/event-confirmation/event-confirmation.component'
import { EventAppointmentComponent } from './events/routes/event-appointment/event-appointment.component';
import { EventsService } from './shared/services/events/events.service';
import { EventPersonalInformationComponent } from './events/routes/event-personal-information/event-personal-information.component';
import { MarketingPreferencesComponent } from './shared/components/marketing-preferences/marketing-preferences.component';
import { ErrorHandlerService } from './shared/services/errors/ErrorHandlerService';
import { AppInsightsMonitoringService } from './shared/services/logging/appInsightsMonitoring.service';
import { HttpAppInsightsMonitorInterceptor } from './shared/services/logging/appInsightsMonitoring.interceptor';
import { AnalyticsService } from './shared/services/analytics/analytics.service';


@NgModule({
    declarations: [
        AppComponent,
        JourneyIndicatorComponent,
        SelectModelComponent,
        NotFoundComponent,
        OptionCardComponent,
        ListingsComponent,
        SelectAppointmentComponent,
        CalendarComponent,
        OptionCardSelectedComponent,
        OptionCardRescheduleComponent,
        PersonalInformationComponent,
        DetailsFormComponent,
        MarketingPreferencesComponent,
        BookingConfirmationComponent,
        LengthRestrictedTextareaComponent,
        PersonalInformationComponent,
        ErrorPageComponent,
        NoTimeslotsMessageComponent,
        PageHeaderComponent,
        BookingCancellationComponent,
        BookingCancellationConfirmationComponent,
        BookingRescheduleComponent,
        PickupLocationComponent,
        EmptyComponent,
        EventsComponent,
        EventAppointmentComponent,
        EventConfirmationComponent,
        EventPersonalInformationComponent
    ],
    imports: [
        AppLoadModule,
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        BmwgMarketingPreferencesModule.forRoot({ theme: environment.brand }),
        BmwgRadiosModule.forRoot({ theme: environment.brand }),
        BmwgTooltipModule.forRoot({ theme: environment.brand })
    ],
    providers: [
        StringsService,
        Utilities,
        UserSelectionsService,
        ReservationService,
        ProductService,
        DatesService,
        E2eService,
        RoutingStateService,
        CancellationService,
        RescheduleService,
        CookieService,
        EventsService,
        AnalyticsService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpE2eInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: isMock ? HttpMockBackendRequestInterceptor : Interceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: Interceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpAppInsightsMonitorInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: ErrorHandlerService,
        },
        ErrorService,
        DeepLinkService,
        AppInsightsMonitoringService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
